const randomId = () => Math.floor(Math.random() * 10000000000);

export const send = (provider, method, params) => {
  return new Promise((resolve, reject) => {
    const payload = {
      id: randomId(),
      method,
      params,
    };
    const callback = (err, result) => {
      if (err) {
        reject(err);
      } else if (result.error) {
        console.error(result.error);
        reject(result.error);
      } else {
        resolve(result.result);
      }
    };

    // Check if the provider itself is an ethers provider (v5 or v6)
    // ethers v5 has getUncheckedSigner, ethers v6 has getNetwork and send(method, params)
    const isEthersProvider = provider.getUncheckedSigner || 
                            (provider.getNetwork && typeof provider.send === 'function');

    if (isEthersProvider) {
      // ethers providers expect send(method, params) and return a Promise
      provider
        .send(method, params)
        .then((r) => resolve(r))
        .catch((e) => reject(e));
      return;
    }

    // For non-ethers providers, get the underlying provider
    const _provider = provider.provider?.provider || provider.provider || provider

    if (_provider.sendAsync) {
      // Legacy providers with sendAsync
      _provider.sendAsync(payload, callback);
    } else if (typeof _provider.send === 'function') {
      // Try sending as payload first (for providers that expect JSON-RPC payload)
      try {
        _provider.send(payload, callback);
      } catch (error) {
        // If that fails, try send(method, params) pattern
        if (
          error.message ===
          "Hardhat Network doesn't support JSON-RPC params sent as an object"
        ) {
          _provider
            .send(method, params)
            .then((r) => resolve(r))
            .catch((e) => reject(e));
        } else {
          reject(error);
        }
      }
    } else {
      reject(new Error('Provider does not support send or sendAsync methods'));
    }
  });
};

const splitSignatureToRSV = (signature) => {
  const r = '0x' + signature.substring(2).substring(0, 64);
  const s = '0x' + signature.substring(2).substring(64, 128);
  const v = parseInt(signature.substring(2).substring(128, 130), 16);
  return { r, s, v };
}

const signWithEthers = async (signer, fromAddress, typeData) => {
  const signerAddress = await signer.getAddress();
  if (signerAddress.toLowerCase() !== fromAddress.toLowerCase()) {
    throw new Error('Signer address does not match requested signing address');
  }

  const { EIP712Domain: _unused, ...types } = typeData.types;
  console.log(signer._signTypedData, typeData);
  const rawSignature = await (signer.signTypedData
    ? signer.signTypedData(typeData.domain, types, typeData.message)
    : signer._signTypedData(typeData.domain, types, typeData.message));

  return splitSignatureToRSV(rawSignature);
}

/**
 * Helper function to serialize typed data with BigInt support
 * Converts BigInt values to strings for JSON serialization
 * This is needed for eth_signTypedData_v4 which requires JSON string format
 */
const serializeTypedData = (typeData) => {
  if (typeof typeData === 'string') {
    return typeData;
  }

  // Recursively convert BigInt values to strings for JSON serialization
  const convertBigInt = (obj) => {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (typeof obj === 'bigint') {
      return obj.toString();
    }
    
    if (Array.isArray(obj)) {
      return obj.map(convertBigInt);
    }
    
    if (typeof obj === 'object') {
      const result = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          result[key] = convertBigInt(obj[key]);
        }
      }
      return result;
    }
    
    return obj;
  };

  const serialized = convertBigInt(typeData);
  return JSON.stringify(serialized);
};

export const signData = async (provider, fromAddress, typeData) => {
  // Check if provider is a signer with signTypedData method
  if (provider._signTypedData || provider.signTypedData) {
    return signWithEthers(provider, fromAddress, typeData);
  }

  // For providers that need JSON string (like MetaMask via eth_signTypedData_v4)
  // Convert BigInt values to strings before JSON.stringify
  const typeDataString = serializeTypedData(typeData);
  const result = await send(provider, 'eth_signTypedData_v4', [fromAddress, typeDataString])
    .catch((error) => {
      if (error.message === 'Method eth_signTypedData_v4 not supported.') {
        // Fallback to eth_signTypedData (also needs serialization)
        const fallbackData = serializeTypedData(typeData);
        return send(provider, 'eth_signTypedData', [fromAddress, fallbackData]);
      } else {
        throw error;
      }
    });

  return {
    r: result.slice(0, 66),
    s: '0x' + result.slice(66, 130),
    v: parseInt(result.slice(130, 132), 16),
  };
};

let chainIdOverride = null;
export const setChainIdOverride = (id) => { chainIdOverride = id };
export const getChainId = async (provider) => chainIdOverride || send(provider, 'eth_chainId');

export const call = (provider, to, data) => send(provider, 'eth_call', [{
  to,
  data,
}, 'latest']);
